import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "motion/react";
import { personalInfo, skillCategories, education, certifications, experiences, ceoEndorsement } from "../../data/portfolioData";
import {
  FaGraduationCap,
  FaAward,
  FaBriefcase,
  FaCode,
  FaMobileAlt,
  FaServer,
  FaTerminal,
  FaCheckCircle,
  FaDownload,
  FaLinkedinIn,
  FaGithub,
  FaQuoteLeft,
  FaEnvelope,
  FaPhoneAlt,
  FaChevronRight,
  FaChevronDown,
  FaShieldAlt,
  FaApple,
  FaBolt,
  FaCube,
  FaEthereum,
} from "react-icons/fa";
import {
  SiReact,
  SiTypescript,
  SiNextdotjs,
  SiExpo,
  SiNodedotjs,
  SiMongodb,
  SiSolidity,
  SiWeb3Dotjs,
  SiIpfs,
  SiDocker,
  SiGithub,
  SiPostman,
  SiLinux,
  SiPython,
} from "react-icons/si";
import pdfCV from "../../Assets/MRayyanCV.pdf";

const renderSkillIcon = (iconName) => {
  switch (iconName) {
    case "SiReact": return <SiReact className="skill-tech-icon react" />;
    case "SiTypescript": return <SiTypescript className="skill-tech-icon ts" />;
    case "SiNextdotjs": return <SiNextdotjs className="skill-tech-icon next" />;
    case "SiExpo": return <SiExpo className="skill-tech-icon expo" />;
    case "FaApple": return <FaApple className="skill-tech-icon apple" />;
    case "SiNodedotjs": return <SiNodedotjs className="skill-tech-icon node" />;
    case "SiMongodb": return <SiMongodb className="skill-tech-icon mongo" />;
    case "FaServer": return <FaServer className="skill-tech-icon server" />;
    case "FaShieldAlt": return <FaShieldAlt className="skill-tech-icon shield" />;
    case "FaBolt": return <FaBolt className="skill-tech-icon bolt" />;
    case "SiSolidity": return <SiSolidity className="skill-tech-icon solidity" />;
    case "SiWeb3Dotjs": return <SiWeb3Dotjs className="skill-tech-icon web3" />;
    case "FaCube": return <FaCube className="skill-tech-icon hardhat" />;
    case "SiIpfs": return <SiIpfs className="skill-tech-icon ipfs" />;
    case "FaEthereum": return <FaEthereum className="skill-tech-icon eth" />;
    case "SiDocker": return <SiDocker className="skill-tech-icon docker" />;
    case "SiGithub": return <SiGithub className="skill-tech-icon git" />;
    case "SiPostman": return <SiPostman className="skill-tech-icon postman" />;
    case "SiLinux": return <SiLinux className="skill-tech-icon linux" />;
    case "SiPython": return <SiPython className="skill-tech-icon python" />;
    default: return <FaCode className="skill-tech-icon default" />;
  }
};

const renderCategoryHeaderIcon = (key) => {
  switch (key) {
    case "mobile": return <FaMobileAlt />;
    case "server": return <FaServer />;
    case "blockchain": return <FaEthereum />;
    case "devops": return <FaTerminal />;
    default: return <FaCode />;
  }
};

function About() {
  const [showSohobyRef, setShowSohobyRef] = useState(true);

  return (
    <section className="about-page-section" id="about">
      <Container>
        {/* Section Header */}
        <motion.div
          className="section-head text-center"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="badge-pill mb-3">
            <span>👨‍💻 Engineering Profile & Expertise</span>
          </div>
          <h1 className="page-title">
            About <span className="gradient-text">Rayyan Waseem</span>
          </h1>
          <p className="page-lead">
            Full Stack & React Native Developer with 3+ years of experience building high-performance mobile applications, decentralized architectures, and scalable backend services.
          </p>
        </motion.div>

        {/* Bio & Summary Row */}
        <Row className="bio-summary-row g-4 align-items-stretch mb-5">
          <Col lg={8}>
            <motion.div
              className="about-bio-card"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4 }}
            >
              <h3>Full Stack & React Native Developer</h3>
              <p className="bio-lead">
                I am a results-driven software engineer based in Lahore, Pakistan with a Bachelor of Science in Computer Science (3.42 GPA) and over 3 years of hands-on software development experience.
              </p>
              <p>
                As a mobile application specialist, I have architected and deployed <strong>5+ live applications on the Apple App Store and Google Play Store</strong>, notably including <strong>Tuba Real Estate</strong>, <strong>BatchBrain</strong>, <strong>ACC App</strong>, <strong>Find My Faheem</strong>, and <strong>LinkMyte</strong>.
              </p>
              <p>
                My background encompasses multi-threaded mobile performance optimization, native OAuth and push notification bridges, enterprise payment gateway integrations, high-throughput Node.js microservices, and audited Web3 smart contracts (Solidity & Hardhat) with proven 50% operational cost reductions.
              </p>

              <div className="about-cta-bar">
                <a
                  href={pdfCV}
                  download="Rayyan_Waseem_CV.pdf"
                  className="btn-modern primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaDownload /> <span>Download Official CV</span>
                </a>
                <a
                  href={personalInfo.socials.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-modern secondary"
                >
                  <FaLinkedinIn /> <span>LinkedIn Profile</span>
                </a>
                <a
                  href={personalInfo.socials.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-modern outline"
                >
                  <FaGithub /> <span>GitHub Profile</span>
                </a>
              </div>
            </motion.div>
          </Col>

          <Col lg={4}>
            <motion.div
              className="about-stats-panel"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="quick-fact-item">
                <span className="fact-label">Location</span>
                <span className="fact-value">{personalInfo.location}</span>
              </div>
              <div className="quick-fact-item">
                <span className="fact-label">Education</span>
                <span className="fact-value">BS Computer Science (GPA: 3.42)</span>
              </div>
              <div className="quick-fact-item">
                <span className="fact-label">Core Stack</span>
                <span className="fact-value text-accent">React Native • TypeScript • Node.js</span>
              </div>
              <div className="quick-fact-item">
                <span className="fact-label">Experience</span>
                <span className="fact-value">3+ Years Building High-Impact Apps</span>
              </div>
              <div className="quick-fact-item">
                <span className="fact-label">Availability</span>
                <span className="fact-value text-emerald">Full-time / Contract / Remote</span>
              </div>
            </motion.div>
          </Col>
        </Row>

        {/* Experience Timeline Section */}
        <div className="experience-timeline-section mt-5 pt-3">
          <div className="section-subhead text-center mb-4">
            <h2 className="section-title">
              Professional <span className="gradient-text">Work Experience</span>
            </h2>
            <p className="section-subtext">Proven track record across leading tech companies, international clients, and decentralized systems.</p>
          </div>

          <div className="timeline-container">
            {experiences.map((exp, idx) => {
              const isSohoby = exp.id === "sohoby-role";

              return (
                <motion.div
                  key={exp.id || idx}
                  className={`timeline-card ${isSohoby ? "sohoby-card-highlight" : ""}`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ duration: 0.35, delay: idx * 0.06 }}
                >
                  <div className="timeline-badge-circle">
                    <FaBriefcase />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <div className="timeline-title-area">
                        <h4 className="role-title">{exp.role}</h4>
                        <h5 className="company-title">
                          {exp.company} <span className="loc-text">• {exp.location}</span>
                        </h5>
                      </div>
                      <span className="period-badge">{exp.period}</span>
                    </div>

                    <p className="exp-desc">{exp.description}</p>

                    <ul className="exp-bullets">
                      {exp.bullets.map((bullet, bIdx) => (
                        <li key={bIdx}>
                          <FaCheckCircle className="bullet-check" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="exp-skills-row">
                      {exp.skills.map((skill, sIdx) => (
                        <span key={sIdx} className="exp-skill-tag">
                          {skill}
                        </span>
                      ))}
                    </div>

                    {/* Integrated Slide-Out Reference for Sohoby IT Solutions */}
                    {isSohoby && (
                      <div className="sohoby-ref-wrapper mt-3 pt-3">
                        <button
                          className="ref-toggle-btn"
                          onClick={() => setShowSohobyRef(!showSohobyRef)}
                        >
                          <FaShieldAlt className="ref-shield-icon" />
                          <span>Professional Reference & CEO Recommendation</span>
                          {showSohobyRef ? <FaChevronDown /> : <FaChevronRight />}
                        </button>

                        <AnimatePresence>
                          {showSohobyRef && (
                            <motion.div
                              className="sohoby-reference-slideout"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25, ease: "easeOut" }}
                            >
                              <div className="ref-card-inner">
                                <div className="ref-quote-header">
                                  <FaQuoteLeft className="ref-quote-icon" />
                                  <span className="ref-verified-tag">✓ Verified CEO Reference</span>
                                </div>
                                <p className="ref-quote-body">"{ceoEndorsement.text}"</p>
                                <div className="ref-author-box">
                                  <div className="ref-avatar">AM</div>
                                  <div className="ref-author-details">
                                    <strong>{ceoEndorsement.name}</strong>
                                    <p>{ceoEndorsement.role} • {ceoEndorsement.location}</p>
                                  </div>
                                  <div className="ref-contact-links">
                                    <a
                                      href={`mailto:${ceoEndorsement.email}`}
                                      className="ref-link-btn"
                                      title="Email Ahmad Mushtaq"
                                    >
                                      <FaEnvelope /> <span>{ceoEndorsement.email}</span>
                                    </a>
                                    <a
                                      href={`tel:${ceoEndorsement.phone.replace(/[^0-9+]/g, '')}`}
                                      className="ref-link-btn"
                                      title="Call Ahmad Mushtaq"
                                    >
                                      <FaPhoneAlt /> <span>{ceoEndorsement.phone}</span>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Technical Skills & Capabilities Matrix */}
        <div className="skills-matrix-section mt-5 pt-4">
          <div className="section-subhead text-center mb-4">
            <h2 className="section-title">
              Technical <span className="gradient-text">Skills & Stack</span>
            </h2>
            <p className="section-subtext">Comprehensive engineering capabilities utilized across production applications.</p>
          </div>

          <Row className="g-4 align-items-stretch">
            {skillCategories.map((cat, idx) => (
              <Col lg={6} md={6} key={idx} className="d-flex">
                <motion.div
                  className="skill-category-card flex-fill"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{ duration: 0.35, delay: idx * 0.06 }}
                >
                  <div className="category-header">
                    <span className="cat-icon-box">{renderCategoryHeaderIcon(cat.iconKey)}</span>
                    <h3>{cat.category}</h3>
                  </div>

                  <div className="skills-list">
                    {cat.skills.map((skill, sIdx) => (
                      <div key={sIdx} className="skill-item-row">
                        <div className="skill-info-left">
                          <span className="skill-icon-wrap">{renderSkillIcon(skill.icon)}</span>
                          <div className="skill-text-block">
                            <strong className="skill-name">{skill.name}</strong>
                            <span className="skill-desc">{skill.desc}</span>
                          </div>
                        </div>
                        <span className={`skill-level-pill ${skill.level.toLowerCase()}`}>
                          {skill.level}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Education & Certifications Row */}
        <Row className="edu-cert-row g-4 mt-4 align-items-stretch">
          <Col md={7} className="d-flex">
            <motion.div
              className="edu-card flex-fill"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.35 }}
            >
              <div className="edu-header">
                <FaGraduationCap className="edu-icon" />
                <div>
                  <h3>Education Background</h3>
                  <p className="edu-institution">Academic Degrees & Honors</p>
                </div>
              </div>
              <div className="edu-list">
                {education.map((edu, eIdx) => (
                  <div key={eIdx} className="edu-entry-item">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <strong className="edu-degree-title">{edu.degree}</strong>
                      <span className="edu-gpa-badge">{edu.gpa}</span>
                    </div>
                    <p className="edu-school-name">{edu.institution} • {edu.location}</p>
                    <p className="edu-details-text">{edu.details}</p>
                    <span className="edu-period-text">{edu.period}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </Col>

          <Col md={5} className="d-flex">
            <motion.div
              className="cert-card flex-fill"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.35, delay: 0.08 }}
            >
              <div className="cert-header">
                <FaAward className="cert-icon" />
                <div>
                  <h3>Certifications & Awards</h3>
                  <p className="cert-sub">Verified Industry Competencies</p>
                </div>
              </div>
              <div className="cert-body">
                {certifications.map((cert, cIdx) => (
                  <div key={cIdx} className="cert-item">
                    <h4>{cert.title}</h4>
                    <p className="cert-issuer">
                      Issued by <strong>{cert.issuer}</strong> • {cert.date}
                    </p>
                    <span className="cert-badge">{cert.badge}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default About;
